import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ClipboardList, CheckCircle2, AlertTriangle, Timer, ChevronDown, ChevronUp, ExternalLink, Wallet, Ban, FileText } from "lucide-react";
import { formatIDR, fmtDate } from "@/lib/constants";
// 2026-06-20 (DUAL WORKFLOW): engine chevron canonical (mirror backend).
import { CHEVRON_UNION, CHEVRON_LABEL, computeWoState, summarizeWoStates } from "@/lib/woWorkflow";

/**
 * WorkingOrderTab — Fase 3. KPI + kartu jumlah per tahap (8) + pencarian/filter
 * + daftar WO + panel detail inline dengan timeline tahapan.
 * Wording wajib user: "Verifikasi – Menunggu PO" (bukan "SPH Disetujui").
 */
export const WO_STAGE8 = [
  { key: "wo_diterima", label: "WO Diterima" },
  { key: "site_survey", label: "Site Survey" },
  { key: "sph_negosiasi", label: "SPH & Negosiasi" },
  { key: "penerbitan_po", label: "Penerbitan PO" },
  { key: "pengerjaan", label: "Pengerjaan" },
  { key: "selesai_fisik", label: "Selesai Fisik" },
  { key: "invoice", label: "Invoice" },
  { key: "closed", label: "Closed" },
];
// 2026-06-20 (DUAL WORKFLOW): kartu "Jumlah WO per Tahap" memakai chevron engine
// canonical (MUTUALLY EXCLUSIVE — satu WO satu chevron). WO_STAGE8 dipertahankan
// HANYA untuk urutan timeline riwayat di panel detail.
const WO_CHEVRONS = [
  ...CHEVRON_UNION.map((k) => ({ key: k, label: CHEVRON_LABEL[k] })),
  { key: "closed", label: "Closed" },
];
const STATUS_CLS = {
  completed: "bg-emerald-50 border-emerald-300 text-emerald-700",
  cancelled_by_owner: "bg-slate-100 border-slate-400 text-slate-600 line-through",
  active: "bg-sky-50 border-sky-300 text-sky-700",
};

function TimelineItem({ label, date, done, active }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`mt-0.5 w-3.5 h-3.5 border-2 shrink-0 ${done ? "bg-emerald-600 border-emerald-600" : active ? "border-[#0B1D3A] bg-white" : "border-slate-300 bg-white"}`} />
      <div className="min-w-0">
        <div className={`text-[11px] font-semibold ${done ? "text-emerald-700" : active ? "text-[#0B1D3A]" : "text-slate-400"}`}>{label}</div>
        <div className="text-[10px] text-slate-400 tabular-nums">{date ? fmtDate(date) : "—"}</div>
      </div>
    </div>
  );
}

export default function WorkingOrderTab({ mon, project, rowAction }) {
  const nav = useNavigate();
  const wos = mon?.wo_list || [];
  const vos = mon?.vo_list || [];
  // 2026-06-20 (user request): project GENERAL (is_outlet=false) tidak memakai WO —
  // tab ini menampilkan data Variation Order; kartu "Jumlah WO per Tahap" hanya utk maintenance.
  const isMaintenance = !!project?.is_outlet;
  const counts = mon?.wo_stage_counts || {};
  const [q, setQ] = useState("");
  const [fStage, setFStage] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [openId, setOpenId] = useState(null);

  // 2026-06-20 (user request DUAL WORKFLOW): ringkasan EKSKLUSIF dari engine
  // canonical backend (`wo_workflow_summary`). Fallback: hitung lokal.
  const wfSum = useMemo(
    () => mon?.wo_workflow_summary || summarizeWoStates(wos.map((w) => computeWoState(w, !!w.overdue))),
    [mon, wos]
  );
  const total = wfSum.total ?? wos.length;
  const pekerjaanAktif = wfSum.pekerjaan_aktif ?? 0;
  const selesaiAdminTerbuka = wfSum.selesai_fisik_admin_terbuka ?? 0;
  const closedCount = wfSum.closed ?? 0;
  const cancelled = wfSum.dibatalkan ?? 0;
  const overdue = wos.filter((w) => w.overdue).length;

  const filtered = useMemo(() => wos.filter((w) => {
    const qlc = q.trim().toLowerCase();
    if (qlc && !`${w.wo_number || ""} ${w.client || ""} ${w.pic || ""}`.toLowerCase().includes(qlc)) return false;
    if (fStage !== "all" && (w.chevron || w.stage) !== fStage) return false;
    // "Aktif" = Pekerjaan Aktif engine: chevron aktif + belum selesai fisik +
    // kategori sudah ditentukan. "Selesai" = selesai fisik (adm terbuka) + Closed.
    if (fStatus === "active" && !(w.chevron !== "closed" && w.chevron !== "cancelled_owner" && w.chevron !== "kategori_belum_ditentukan" && w.work_status !== "Selesai Fisik")) return false;
    if (fStatus === "closed" && !(w.work_status === "Selesai Fisik" || w.chevron === "closed")) return false;
    if (fStatus === "overdue" && !w.overdue) return false;
    return true;
  }), [wos, q, fStage, fStatus]);

  const stageIdx = (k) => WO_STAGE8.findIndex((x) => x.key === k);

  // ---- Tampilan PROJECT GENERAL: daftar Variation Order (tanpa kartu tahap WO) ----
  if (!isMaintenance) {
    const totalVO = vos.length;
    const aktifVO = vos.filter((v) => (v.status || "active") === "active").length;
    const nilaiVO = vos.reduce((a, v) => a + Number(v.contract_value || 0), 0);
    const incVO = vos.reduce((a, v) => a + Number(v.total_income || 0), 0);
    return (
      <div className="space-y-4" data-testid="variation-order-tab">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { icon: ClipboardList, label: "Total VO", value: totalVO, testid: "vo-total" },
            { icon: Timer, label: "Aktif", value: aktifVO, testid: "vo-aktif" },
            { icon: Wallet, label: "Nilai VO", value: nilaiVO > 0 ? formatIDR(nilaiVO) : "—", testid: "vo-nilai" },
            { icon: CheckCircle2, label: "Uang Masuk VO", value: formatIDR(incVO), tone: "green", testid: "vo-uang-masuk" },
          ].map((k) => (
            <div key={k.label} className="bg-white border border-slate-200 p-3" data-testid={k.testid}>
              <div className="flex items-center gap-1.5">
                <k.icon size={13} className={k.tone === "green" ? "text-emerald-700" : "text-[#0B1D3A]"} />
                <span className="overline text-[9px] text-slate-500 tracking-wider">{k.label}</span>
              </div>
              <div className={`font-display text-lg tabular-nums mt-1 ${k.tone === "green" ? "text-emerald-700" : "text-[#0B1D3A]"}`}>{k.value}</div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200" data-testid="vo-list">
          {vos.length === 0 ? (
            <div className="p-6 text-center text-[11px] text-slate-400" data-testid="vo-empty">Belum ada Variation Order pada project ini.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">{["Variation Order", "Periode", "Nilai Kontrak", "Uang Masuk", "PIC", "Status"].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
              </thead>
              <tbody>
                {vos.map((v) => {
                  const stKey = v.status || "active";
                  const stLabel = stKey === "completed" ? "Selesai" : (stKey === "cancelled" || stKey === "cancelled_by_owner") ? "Dibatalkan" : "Aktif";
                  const stCls = stKey === "completed" ? STATUS_CLS.completed : (stKey === "cancelled" || stKey === "cancelled_by_owner") ? STATUS_CLS.cancelled_by_owner : STATUS_CLS.active;
                  return (
                    <tr key={v.id} data-testid={`vo-row-${v.id}`} onClick={() => nav(`/projects/${v.id}/monitoring`)}
                      title="Klik untuk membuka Dashboard Monitoring VO ini"
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                      <td className="px-3 py-2"><div className="font-medium text-[#0B1D3A]">{v.variation_order_name || `#${v.id.slice(0, 6)}`}</div></td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">{v.vo_start_date ? fmtDate(v.vo_start_date) : "—"} – {v.vo_end_date ? fmtDate(v.vo_end_date) : "—"}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">{v.contract_value > 0 ? formatIDR(v.contract_value) : "—"}</td>
                      <td className="px-3 py-2 tabular-nums text-emerald-700">{formatIDR(v.total_income || 0)}</td>
                      <td className="px-3 py-2 text-slate-700">{v.pic || "—"}</td>
                      <td className="px-3 py-2"><span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${stCls}`} data-testid={`vo-status-${v.id}`}>{stLabel}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ---- Tampilan PROJECT MAINTENANCE: KPI + kartu tahap + daftar WO ----

  return (
    <div className="space-y-4" data-testid="working-order-tab">
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          { icon: ClipboardList, label: "Total WO", value: total, testid: "wo-total" },
          { icon: Timer, label: "Pekerjaan Aktif", value: pekerjaanAktif, testid: "wo-aktif" },
          { icon: FileText, label: "Selesai Fisik · Adm Terbuka", value: selesaiAdminTerbuka, testid: "wo-selesai-admin" },
          { icon: CheckCircle2, label: "Closed", value: closedCount, tone: "green", testid: "wo-selesai" },
          { icon: Ban, label: "Dibatalkan", value: cancelled, testid: "wo-dibatalkan" },
          { icon: AlertTriangle, label: "Terlambat", value: overdue, tone: overdue > 0 ? "red" : undefined, testid: "wo-terlambat" },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 p-3" data-testid={k.testid}>
            <div className="flex items-center gap-1.5">
              <k.icon size={13} className={k.tone === "red" ? "text-rose-700" : k.tone === "green" ? "text-emerald-700" : "text-[#0B1D3A]"} />
              <span className="overline text-[9px] text-slate-500 tracking-wider">{k.label}</span>
            </div>
            <div className={`font-display text-xl tabular-nums mt-1 ${k.tone === "red" ? "text-rose-700" : k.tone === "green" ? "text-emerald-700" : "text-[#0B1D3A]"}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Jumlah per tahap — klik untuk filter */}
      <div className="bg-white border border-slate-200 p-4" data-testid="wo-stage-cards">
        <h3 className="text-sm font-semibold text-[#0B1D3A] mb-3">Jumlah WO per Tahap</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
          {WO_CHEVRONS.map((st) => (
            <button key={st.key} type="button" data-testid={`wo-stage-${st.key}`}
              onClick={() => setFStage(fStage === st.key ? "all" : st.key)}
              className={`border p-2.5 text-center transition-colors ${fStage === st.key ? "border-[#0B1D3A] bg-[#0B1D3A]/5" : "border-slate-200 hover:border-[#0B1D3A]/40"}`}>
              <div className="font-display text-2xl tabular-nums text-[#0B1D3A]">{counts[st.key] || 0}</div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5 leading-tight">{st.label}</div>
            </button>
          ))}
          {/* 2026-06-20 (user request): kartu Dibatalkan — invarian Σ tahap + dibatalkan = total WO. */}
          <button type="button" data-testid="wo-stage-cancelled_owner"
            onClick={() => setFStage(fStage === "cancelled_owner" ? "all" : "cancelled_owner")}
            className={`border p-2.5 text-center transition-colors ${fStage === "cancelled_owner" ? "border-slate-500 bg-slate-100" : "border-slate-300 bg-slate-50 hover:border-slate-500/60"}`}>
            <div className="font-display text-2xl tabular-nums text-slate-500">{counts.cancelled_owner ?? cancelled}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5 leading-tight">Dibatalkan</div>
          </button>
        </div>
      </div>

      {/* Search & filter */}
      <div className="bg-white border border-slate-200 p-3 flex items-center gap-2 flex-wrap" data-testid="wo-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-2.5 top-2 text-slate-400 pointer-events-none" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nomor WO, client, PIC…"
            data-testid="wo-search" className="w-full pl-8 pr-3 py-1.5 border border-slate-300 text-xs focus:outline-none focus:border-[#0B1D3A]" />
        </div>
        <select value={fStage} onChange={(e) => setFStage(e.target.value)} data-testid="wo-filter-stage" className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          <option value="all">Semua Tahap</option>
          {WO_CHEVRONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          <option value="cancelled_owner">Dibatalkan</option>
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="wo-filter-status" className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          <option value="all">Semua Status</option>
          <option value="active">Aktif</option>
          <option value="overdue">Terlambat</option>
          <option value="closed">Selesai</option>
        </select>
        <span className="text-[10px] text-slate-400 tabular-nums">{filtered.length} WO</span>
      </div>

      {/* Daftar WO */}
      <div className="bg-white border border-slate-200" data-testid="wo-list">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-[11px] text-slate-400" data-testid="wo-empty">
            {wos.length === 0 ? "Belum ada Working Order pada project ini." : "Tidak ada WO yang cocok dengan filter."}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">{["WO", "Nilai", "Tahap Saat Ini", "PIC", "Hari Menunggu", "Target", "Status", ""].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((w) => {
                const isOpen = openId === w.id;
                const cur = stageIdx(w.stage);
                const tl = [
                  { label: "WO Diterima", date: w.created_at, done: cur >= 0 },
                  { label: "Site Survey", date: w.site_survey_date, done: cur > 0 },
                  { label: "SPH & Negosiasi", date: w.sph_submit_date, done: cur > 1 },
                  // Wording wajib: "Verifikasi – Menunggu PO".
                  { label: "Verifikasi – Menunggu PO", date: w.po_number ? w.updated_at : null, done: cur > 2 },
                  { label: "Pengerjaan", date: w.work_start_date, done: cur > 3 },
                  { label: "Selesai Fisik", date: w.project_done_date, done: cur > 4 },
                  { label: "Invoice", date: w.invoice_sent_date, done: cur > 5 },
                  { label: "Closed", date: w.stage === "closed" ? w.updated_at : null, done: cur > 6 },
                ];
                return [
                  <tr key={w.id} data-testid={`wo-row-${w.id}`} onClick={() => setOpenId(isOpen ? null : w.id)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2"><div className="font-medium text-[#0B1D3A]">{w.wo_number || `#${w.id.slice(0, 6)}`}</div><div className="text-[10px] text-slate-500">{w.client || ""}</div></td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{w.contract_value > 0 ? formatIDR(w.contract_value) : "—"}</td>
                    <td className="px-3 py-2"><span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-slate-50 border-slate-300 text-slate-700">{w.chevron_label || WO_STAGE8[cur]?.label || w.stage}</span></td>
                    <td className="px-3 py-2 text-slate-700">{w.pic || "—"}</td>
                    <td className="px-3 py-2 tabular-nums"><span className={w.days_waiting > 14 && w.stage !== "closed" ? "text-amber-700 font-semibold" : "text-slate-600"}>{w.days_waiting ?? "—"}</span></td>
                    <td className="px-3 py-2 tabular-nums text-slate-500">{w.target_date ? fmtDate(w.target_date) : "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${STATUS_CLS[w.status] || STATUS_CLS.active}`}>{w.status === "completed" ? "Selesai" : w.status === "cancelled_by_owner" ? "Dibatalkan" : "Aktif"}</span>
                      {w.needs_verification && <span data-testid={`wo-verify-${w.id}`} className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-amber-50 border-amber-300 text-amber-800" title={(w.verification_reasons || []).join("\n")}>Perlu Verifikasi</span>}
                      {(w.late_status || w.overdue) && <span className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-rose-50 border-rose-300 text-rose-700">{w.late_status || "Terlambat"}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-400">
                      <div className="flex items-center justify-end gap-2">
                        {rowAction ? rowAction(w) : null}
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={`${w.id}-detail`} data-testid={`wo-detail-${w.id}`} className="border-b border-slate-100 bg-slate-50/60">
                      <td colSpan={8} className="px-4 py-4">
                        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Timeline Tahapan</div>
                          {/* 2026-06-20 (user request): lompat ke halaman Summary Project → tab Monitoring WO, langsung membuka WO ini (deep-link ?tab=wo&wo=<id>). */}
                          <button type="button" data-testid={`wo-open-monitoring-${w.id}`}
                            onClick={() => nav(`/projects?tab=wo&wo=${w.id}`)}
                            title="Buka WO ini di halaman Summary Project → Monitoring WO"
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">
                            <ExternalLink size={11} /> Buka di Monitoring WO
                          </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {tl.map((t, i) => <TimelineItem key={i} {...t} active={i === cur} />)}
                        </div>
                        {w.po_number && <div className="mt-3 text-[11px] text-slate-600">Nomor PO: <span className="font-mono font-semibold text-[#0B1D3A]">{w.po_number}</span></div>}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
